import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { LogOut, Pencil, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { RhfStateCitySelect } from '@/components/common/RhfStateCitySelect';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmButton,
  DatePicker,
  Input,
  SelectField,
  Spinner,
  useToast,
} from '@/components/ui';
import {
  buildUserEditPayload,
  userEditSchema,
  type UserEditValues,
} from '@/features/users/userEditSchema';
import useAuth from '@/hooks/useAuth';
import { useDesignations } from '@/hooks/useCompanySettings';
import { useMySessions, useRevokeMySession } from '@/hooks/useSessions';
import { useUpdateUser } from '@/hooks/useUsers';
import { optionsWithStored } from '@/lib/designations';
import { useAppDispatch, useAppSelector } from '@/store';
import { setAuth } from '@/store/authSlice';
import { formatDate, formatDateTime, todayIso } from '@/utils/format';
import { deviceTypeIcon, deviceTypeLabel, formatBrowser, formatLocation } from '@/utils/session';

const ProfilePage = () => {
  const { user } = useAuth();
  const isAdmin = useAppSelector((state) => state.auth.user?.role === 'admin');
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { data: sessions, isLoading: sessionsLoading } = useMySessions();
  const revokeSession = useRevokeMySession();
  const updateUser = useUpdateUser();
  const { data: designations, isLoading: designationsLoading } = useDesignations();
  const [editing, setEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<UserEditValues>({ resolver: zodResolver(userEditSchema) });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email ?? '',
        dateOfBirth: user.dateOfBirth ?? '',
        dateOfJoining: user.dateOfJoining ?? '',
        designationLevel: user.designationLevel ?? '',
        designationDept: user.designationDept ?? '',
        address: user.address ?? '',
        pincode: user.pincode ?? '',
        city: user.city ?? '',
        state: user.state ?? '',
        employeeCode: user.employeeCode ?? '',
      });
    }
  }, [user, reset]);

  const onRevoke = (id: number) => {
    revokeSession.mutate(id, {
      onSuccess: () => toast.success('Device signed out'),
      onError: () => toast.error('Could not sign out that device. Please try again.'),
    });
  };

  const onSubmit = async (values: UserEditValues): Promise<void> => {
    if (!user) return;
    const payload = buildUserEditPayload(values);
    try {
      const updated = await updateUser.mutateAsync({ id: user.id, payload });
      // Refresh the Redux auth user so the profile reflects edits immediately,
      // without a reload (same pattern as useCompleteOnboarding).
      dispatch(setAuth({ accessToken: accessToken ?? '', user: updated }));
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      const message =
        err instanceof AxiosError
          ? ((err.response?.data as { message?: string })?.message ?? 'Update failed')
          : 'Update failed';
      toast.error(message);
    }
  };

  const field = (label: string, value: string | null | undefined) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink/40">{label}</span>
      <span className="text-sm font-medium text-ink">{value || '-'}</span>
    </div>
  );

  const metaField = (label: string, value: ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink/40">{label}</span>
      {value}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">My profile</h1>
      <p className="mt-1.5 text-sm text-ink/60">
        {isAdmin
          ? 'Your account details. Use the Edit button below to update them.'
          : 'Your account details. Contact your admin to make any changes.'}
      </p>

      <Card className="mt-6">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar name={user?.name ?? 'User'} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-lg font-bold text-ink">{user?.name ?? '-'}</p>
            <p className="truncate text-sm text-ink/60">+91 {user?.mobile ?? '-'}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3">
          {metaField(
            'Role',
            <Badge
              variant={user?.role === 'admin' ? 'info' : 'neutral'}
              className="w-fit capitalize"
            >
              {user?.role ?? 'user'}
            </Badge>,
          )}
          {metaField(
            'Job role',
            <span className="text-sm font-medium text-ink">{user?.jobRole || '-'}</span>,
          )}
          {metaField(
            'Member since',
            <span className="text-sm font-medium text-ink">
              {user?.createdAt ? formatDate(user.createdAt) : '-'}
            </span>,
          )}
        </div>
      </Card>

      <Card className="mt-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold tracking-tight text-ink">Profile details</h2>
          {isAdmin && user?.id && !editing && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit my profile
            </Button>
          )}
          {isAdmin && editing && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false);
                reset();
              }}
              className="flex items-center gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
        </div>

        {isAdmin && editing ? (
          <form
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e).catch(() => {});
            }}
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Full name"
                type="text"
                error={errors.name?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('name')}
              />
              <Input
                label="Email address"
                type="email"
                error={errors.email?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('email')}
              />
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: dateField }) => (
                  <DatePicker
                    label="Date of birth"
                    value={dateField.value}
                    onChange={dateField.onChange}
                    max={todayIso()}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="dateOfJoining"
                render={({ field: dateField }) => (
                  <DatePicker
                    label="Date of joining"
                    value={dateField.value}
                    onChange={dateField.onChange}
                    max={todayIso()}
                    error={errors.dateOfJoining?.message}
                  />
                )}
              />
              <Controller
                name="designationLevel"
                control={control}
                render={({ field: selected }) => (
                  <SelectField
                    label="Designation level"
                    disabled={designationsLoading}
                    error={errors.designationLevel?.message}
                    value={selected.value ?? ''}
                    onChange={(e) => selected.onChange(e.target.value)}
                  >
                    <option value="">Not set</option>
                    {/* The stored value is appended when the configured list no
                    longer offers it - see lib/designations. Without it the
                    select would silently show "Not set" and saving would clear
                    a department the employee really has. */}
                    {optionsWithStored(designations?.prefixes ?? [], user?.designationLevel).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </SelectField>
                )}
              />
              {/* Editable only because this whole form is behind `isAdmin`
                  above. Department is otherwise derived from the job role and
                  user.controller.ts drops the field from a non-admin's own
                  update - keep any future un-gating of the form in step with
                  that guard, or the select saves silently and changes nothing. */}
              <Controller
                name="designationDept"
                control={control}
                render={({ field: selected }) => (
                  <SelectField
                    label="Department"
                    disabled={designationsLoading}
                    error={errors.designationDept?.message}
                    value={selected.value ?? ''}
                    onChange={(e) => selected.onChange(e.target.value)}
                  >
                    <option value="">Not set</option>
                    {optionsWithStored(designations?.roles ?? [], user?.designationDept).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </SelectField>
                )}
              />
              <Input
                label="Pincode"
                type="text"
                inputMode="numeric"
                error={errors.pincode?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('pincode')}
              />
              {/* Dropdowns, not free text: see components/common/StateCitySelect. */}
              <RhfStateCitySelect
                control={control}
                setValue={setValue}
                stateName="state"
                cityName="city"
                labelClassName="text-sm font-medium text-foreground"
                stateError={errors.state?.message}
                cityError={errors.city?.message}
              />
              <Input
                label="Posting location address"
                type="text"
                wrapperClassName="sm:col-span-2"
                error={errors.address?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('address')}
              />
              <Input
                label="Employee code"
                type="text"
                maxLength={10}
                placeholder="e.g. SRK"
                wrapperClassName="sm:col-span-2"
                helperText="Unique across the company. Saving a code another employee already holds is rejected."
                error={errors.employeeCode?.message}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('employeeCode')}
              />
            </div>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty}
              className="self-start"
            >
              Save changes
            </Button>
          </form>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {field('Full name', user?.name)}
            {field('Email address', user?.email)}
            {field('Date of birth', user?.dateOfBirth)}
            {field('Date of joining', user?.dateOfJoining)}
            {field('Designation level', user?.designationLevel)}
            {field('Department', user?.designationDept)}
            {field('Pincode', user?.pincode)}
            {field('City', user?.city)}
            {field('State', user?.state)}
            <div className="sm:col-span-2">{field('Posting location address', user?.address)}</div>
            <div className="sm:col-span-2">{field('Employee code', user?.employeeCode)}</div>
          </div>
        )}
      </Card>

      <Card className="mt-6">
        <h2 className="text-base font-bold tracking-tight text-ink">My devices</h2>
        <p className="mt-1 mb-4 text-sm text-ink/60">
          Everywhere you&apos;re signed in. Sign out of any device you don&apos;t recognise.
        </p>

        {sessionsLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {(sessions ?? []).map((session) => {
              const Icon = deviceTypeIcon(session.deviceType);
              return (
                <li
                  key={session.id}
                  className="flex flex-col gap-3 rounded-xl border border-line bg-cream/40 px-4 py-3 transition-colors hover:bg-cream sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-white text-ink/50">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-ink">
                        {deviceTypeLabel(session.deviceType)}
                        {' · '}
                        {formatBrowser(session)}
                        {session.current && <Badge variant="success">This device</Badge>}
                      </div>
                      <p className="text-xs text-ink/60">
                        {formatLocation(session)}
                        {' · Active '}
                        {formatDateTime(session.lastActiveAt)}
                      </p>
                    </div>
                  </div>
                  {!session.current && (
                    <ConfirmButton
                      variant="ghost"
                      size="sm"
                      disabled={revokeSession.isPending && revokeSession.variables === session.id}
                      onConfirm={() => onRevoke(session.id)}
                      className="flex shrink-0 items-center gap-1.5 text-danger hover:text-danger"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </ConfirmButton>
                  )}
                </li>
              );
            })}
            {(sessions ?? []).length === 0 && (
              <p className="text-center text-sm text-ink/50">No active sessions.</p>
            )}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default ProfilePage;
